import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDownloadCsvMutation } from '../../../../hooks/mutations/useDownloadMutation';
import { useArchiveGnrRequestMutation, useUpdateGnrRequestMutation } from '../../../../hooks/mutations/useGnrRequestMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { GnrRequest } from '../../../../services/apiSLTP/sites/gnrRequests';
import { GnrUsage } from '../../../../services/apiSLTP/sites/gnrUsages';
import { GnrUtilityVehicleUsage } from '../../../../services/apiSLTP/sites/gnrUtilityVehicleUsages';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getGnrStateColor } from '../../../../utils/colors';
import { exportTypeEnum, getGnrPeriodInfos, getGnrStateInfos, gnrState, permissionLevels } from '../../../../utils/enums';
import ExportCsvIcon from '../../../ExportCsvIcon';
import IconLine from '../../../IconLine';
import StateCell from '../../../cells/StateCell';
import ModalBody from '../../../modals/ModalBody';
import ConfirmGnrRequestModal from '../../../modals/operations/gnr/ConfirmGnrRequestModal';
import ManageGnrPlannedModal from '../../../modals/operations/gnr/ManageGnrPlannedModal';
import ManageGnrRequestModal from '../../../modals/operations/gnr/ManageGnrRequestModal';
import IconButton from '../../../shared/IconButton';
import GenericAgGridTable from '../../../tables/GenericAgGridTable';
import ValidateDeliveryGnrRequestModal from '../../../modals/operations/gnr/ValidateDeliveryGnrRequestModal';
import { useBoolean } from '../../../../hooks/useBoolean';

interface GnrTableProps {
    dataGnrRequests?: GnrRequest[];
    state: number;
    refetch: () => void;
    isLoading: boolean;
    storageKey: string;
}

const GnrTable: React.FC<GnrTableProps> = ({ dataGnrRequests, state, refetch, isLoading, storageKey }) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [selectedGnrRequest, setSelectedGnrRequest] = useState<GnrRequest | null>(null);

    const [showConfirmDeliveryModal, setShowConfirmDeliveryModal] = useState(false);
    const [showManageGnrPlannedModal, setShowManageGnrPlannedModal] = useState(false);
    const [showCancelGnrRequestModal, setShowCancelGnrRequestModal] = useState(false);
    const [showDeliveryNoteModal, setShowDeliveryNoteModal] = useState(false);
    const [showCreateGnrRequestModal, setShowCreateGnrRequestModal] = useState(false);
    const validateDeliveryModal = useBoolean(false);

    const { mutate: exportCsv, isLoading: isExportCsvLoading } = useDownloadCsvMutation();
    const { mutate: mutateGnrRequest } = useUpdateGnrRequestMutation({ onSettled: refetch });
    const { mutate: archiveGnrRequest } = useArchiveGnrRequestMutation({ onSettled: refetch });

    const columnDefs: ColDef<GnrRequest>[] = [
        {
            headerName: capitalizeFirstLetter(t('site')),
            field: 'siteId.name',
            colId: 'site',
            initialFlex: 1,
            initialWidth: 400,
            minWidth: 200,
        },
        {
            headerName: capitalizeFirstLetter(t('address')),
            field: 'siteId.address',
            colId: 'address',
            initialWidth: 400,
        },
        {
            headerName: capitalizeFirstLetter(t('createdAt')),
            field: 'createdAt',
            valueFormatter: (params) =>
                capitalizeFirstLetter(
                    params.value
                        ? new Date(params.value).toLocaleString(t('localeDate'), {
                              weekday: 'long',
                              month: 'numeric',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                          })
                        : ''
                ),
            filter: 'agTextColumnFilter',
            enableRowGroup: false,
            enableValue: false,
            colId: 'createdAt',
            cellClass: 'font-w-700',
            minWidth: 250,
        },
        {
            headerName: capitalizeFirstLetter(t('requested')),
            field: 'requestDate',
            colId: 'requested',
            headerStyle: { color: getGnrStateColor(gnrState.REQUESTED) },
            initialWidth: 250,
            filter: 'agTextColumnFilter',
            sortable: false,
            enableRowGroup: false,
            enableValue: false,
            valueGetter: (params) =>
                `${capitalizeFirstLetter(
                    new Date(params.data?.requestDate || new Date()).toLocaleDateString(t('localeDate'), {
                        weekday: 'long',
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric',
                    })
                )}  ${capitalizeFirstLetter(t(getGnrPeriodInfos(params.data?.requestPeriod)))} ${params.data?.requestVolume}L ${capitalizeFirstLetter(t('by')) + ' ' + formatName(params.data?.requesterId.firstName, params.data?.requesterId.lastName)}`,
            cellRenderer: (params: ICellRendererParams<GnrRequest>) => {
                if (!params.data) return null;
                return (
                    <>
                        <div className="font-w-700 fx-r">
                            <div className="pad-r-3px">{capitalizeFirstLetter(t('forThe'))}</div>
                            {capitalizeFirstLetter(
                                new Date(params.data.requestDate).toLocaleDateString(t('localeDate'), {
                                    weekday: 'long',
                                    month: 'numeric',
                                    day: 'numeric',
                                    year: 'numeric',
                                })
                            )}
                        </div>
                        <div className="font-italic">{capitalizeFirstLetter(t(getGnrPeriodInfos(params.data.requestPeriod)))}</div>
                        <div>{params.data.requestVolume}L</div>
                        <div>
                            {capitalizeFirstLetter(t('by')) + ' ' + formatName(params.data.requesterId.firstName, params.data.requesterId.lastName)}
                        </div>
                    </>
                );
            },
        },
    ];

    if ([gnrState.IN_PROGRESS, gnrState.DELIVERED, gnrState.CANCELED].includes(state)) {
        columnDefs.push({
            headerName: capitalizeFirstLetter(t('planned')),
            field: 'plannedDate',
            colId: 'planned',
            initialWidth: 250,
            filter: 'agTextColumnFilter',
            sortable: false,
            enableRowGroup: false,
            enableValue: false,
            headerStyle: { color: getGnrStateColor(gnrState.IN_PROGRESS) },
            valueGetter: (params) =>
                `${
                    params.data?.plannedDate
                        ? capitalizeFirstLetter(
                              new Date(params.data.plannedDate).toLocaleDateString(t('localeDate'), {
                                  weekday: 'long',
                                  month: 'numeric',
                                  day: 'numeric',
                                  year: 'numeric',
                              })
                          )
                        : '-'
                }  ${params.data?.plannedPeriod ? capitalizeFirstLetter(t(getGnrPeriodInfos(params.data.plannedPeriod))) : '-'} ${params.data?.plannedVolume ? `${params.data.plannedVolume}L` : '-'} ${
                    params.data?.plannerId
                        ? capitalizeFirstLetter(t('by')) + ' ' + formatName(params.data.plannerId?.firstName, params.data.plannerId?.lastName)
                        : '-'
                }`,
            cellRenderer: (params: ICellRendererParams<GnrRequest>) => {
                if (!params.data) return null;
                return (
                    <>
                        <div className="font-w-700">
                            {params.data.plannedDate
                                ? capitalizeFirstLetter(
                                      new Date(params.data.plannedDate).toLocaleDateString(t('localeDate'), {
                                          weekday: 'long',
                                          month: 'numeric',
                                          day: 'numeric',
                                          year: 'numeric',
                                      })
                                  )
                                : '-'}
                        </div>
                        <div className="font-italic">
                            {params.data.plannedPeriod ? capitalizeFirstLetter(t(getGnrPeriodInfos(params.data.plannedPeriod))) : '-'}
                        </div>
                        <div>{params.data.plannedVolume ? `${params.data.plannedVolume}L` : '-'}</div>
                        <div>
                            {params.data.plannerId
                                ? capitalizeFirstLetter(t('by')) + ' ' + formatName(params.data.plannerId?.firstName, params.data.plannerId?.lastName)
                                : '-'}
                        </div>
                    </>
                );
            },
        });
    }

    if ([gnrState.DELIVERED, gnrState.CANCELED].includes(state)) {
        columnDefs.push(
            {
                headerName: capitalizeFirstLetter(t('delivered')),
                field: 'deliveryDate',
                colId: 'delivered',
                initialWidth: 250,
                filter: 'agTextColumnFilter',
                sortable: false,
                enableRowGroup: false,
                enableValue: false,
                headerStyle: { color: getGnrStateColor(gnrState.DELIVERED) },
                valueGetter: (params) =>
                    `${
                        params.data?.deliveryDate
                            ? capitalizeFirstLetter(
                                  new Date(params.data.deliveryDate).toLocaleDateString(t('localeDate'), {
                                      weekday: 'long',
                                      month: 'numeric',
                                      day: 'numeric',
                                      year: 'numeric',
                                  })
                              )
                            : '-'
                    } ${params.data?.receivedVolume ? `${params.data.receivedVolume}L` : '-'} ${
                        params.data?.receiverId
                            ? capitalizeFirstLetter(t('validatedBy')) +
                              ' ' +
                              formatName(params.data.receiverId?.firstName, params.data.receiverId?.lastName)
                            : '-'
                    }`,
                cellRenderer: (params: ICellRendererParams<GnrRequest>) => {
                    if (!params.data) return null;
                    return (
                        <>
                            <div className="font-w-700">
                                {params.data.deliveryDate
                                    ? capitalizeFirstLetter(
                                          new Date(params.data.deliveryDate).toLocaleDateString(t('localeDate'), {
                                              weekday: 'long',
                                              month: 'numeric',
                                              day: 'numeric',
                                              year: 'numeric',
                                          })
                                      )
                                    : '-'}
                            </div>
                            <div className="font-italic">-</div>
                            <div>{params.data.receivedVolume ? `${params.data.receivedVolume}L` : '-'}</div>
                            <div>
                                {params.data.receiverId
                                    ? capitalizeFirstLetter(t('validatedBy')) +
                                      ' ' +
                                      formatName(params.data.receiverId?.firstName, params.data.receiverId?.lastName)
                                    : '-'}
                            </div>
                        </>
                    );
                },
            },
            {
                headerName: capitalizeFirstLetter(t('deliveredByMachine')),
                colId: 'deliveredByMachine',
                initialWidth: 300,
                filter: 'agTextColumnFilter',
                sortable: false,
                enableRowGroup: false,
                enableValue: false,
                valueGetter: (params) => params.data?.gnrUsages?.map((value) => value.machineId.designation + ' ' + value.usedVolume + 'L'),
                cellRenderer: (params: ICellRendererParams<GnrRequest>) => {
                    if (!params.data) return null;
                    return (
                        <div className="fx-c gap-5px">
                            {params.data.gnrUsages?.map((gnrUsage: GnrUsage, index: number) => (
                                <div key={gnrUsage._id} className="border-radius-10px bg-gray fx-c" style={{ paddingLeft: 5, paddingRight: 5 }}>
                                    <div>{gnrUsage.usedVolume}L</div>
                                    <div className="one-line">{gnrUsage.machineId.designation}</div>
                                </div>
                            ))}
                        </div>
                    );
                },
            },
            {
                headerName: capitalizeFirstLetter(t('deliveredByVehicle')),
                colId: 'deliveredByVehicle',
                initialWidth: 300,
                filter: 'agTextColumnFilter',
                sortable: false,
                enableRowGroup: false,
                enableValue: false,
                valueGetter: (params) =>
                    params.data?.gnrUtilityVehicleUsages?.map((value) => value.utilityVehicleId.designation + ' ' + value.usedVolume + 'L'),
                cellRenderer: (params: ICellRendererParams<GnrRequest>) => {
                    if (!params.data) return null;
                    return (
                        <div className="fx-c gap-5px">
                            {params.data.gnrUtilityVehicleUsages?.map((gnrUtilityVehicleUsage: GnrUtilityVehicleUsage, index: number) => (
                                <div
                                    key={gnrUtilityVehicleUsage._id}
                                    className="border-radius-10px bg-gray fx-c"
                                    style={{ paddingLeft: 5, paddingRight: 5 }}
                                >
                                    <div>{gnrUtilityVehicleUsage.usedVolume}L</div>
                                    <div className="one-line">{gnrUtilityVehicleUsage.utilityVehicleId.designation}</div>
                                </div>
                            ))}
                        </div>
                    );
                },
            },
            {
                headerName: capitalizeFirstLetter(t('deliveryNote')),
                colId: 'deliveryNote',
                minWidth: 300,
                filter: false,
                sortable: false,
                enableRowGroup: false,
                enableValue: false,
                cellRenderer: (params: ICellRendererParams<GnrRequest>) => {
                    const data = params.data;
                    if (!data) return null;

                    return (
                        <>
                            {data.deliveryReceiptPhotoFile?.fileName && (
                                <IconButton
                                    iconName="image"
                                    onClick={() => {
                                        setSelectedGnrRequest(data);
                                        setShowDeliveryNoteModal(true);
                                    }}
                                    title={capitalizeFirstLetter(t('seeTheDeliveryNote'))}
                                />
                            )}
                        </>
                    );
                },
            }
        );
    }

    if (state === gnrState.DELIVERED) {
        columnDefs.push({
            headerName: capitalizeFirstLetter(t('observations')),
            field: 'receivedObservations',
            colId: 'observations',
            filter: 'agTextColumnFilter',
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        });
    }

    if ([gnrState.IN_PROGRESS, gnrState.DELIVERED, gnrState.CANCELED].includes(state)) {
        columnDefs.push({
            headerName: capitalizeFirstLetter(t('supplier')),
            colId: 'supplier',
            valueGetter: (params) => `${params.data?.gnrSupplierId?.name} ${params.data?.gnrSupplierId?.area}`,
            cellRenderer: (params: ICellRendererParams<GnrRequest>) => {
                if (!params.data) return params.value;
                return (
                    <>
                        <div>{params.data?.gnrSupplierId?.name}</div>
                        <div>{params.data?.gnrSupplierId?.area}</div>
                    </>
                );
            },
        });
    }

    columnDefs.push({
        headerName: capitalizeFirstLetter(t('status')),
        valueGetter: (params) => capitalizeFirstLetter(t(getGnrStateInfos(params.data?.state))),
        cellRenderer: (params: ICellRendererParams<GnrRequest>) => {
            if (!params.data) return params.value;
            return <StateCell stateColor={getGnrStateColor(params.data?.state)} text={t(getGnrStateInfos(params.data?.state))} />;
        },
        colId: 'status',
    });

    columnDefs.push({
        headerName: capitalizeFirstLetter(t('actions')),
        colId: 'actions',
        sortable: false,
        filter: false,
        enableRowGroup: false,
        enableValue: false,
        cellRenderer: (params: ICellRendererParams<GnrRequest>) => {
            if (!params.data) return null;
            return (
                <IconLine>
                    {[gnrState.REQUESTED, gnrState.IN_PROGRESS].includes(state) && (
                        <IconButton
                            iconName="editNote"
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedGnrRequest(params.data);
                                setShowCreateGnrRequestModal(true);
                            }}
                            title={capitalizeFirstLetter(t('editRequest'))}
                        />
                    )}
                    {[gnrState.IN_PROGRESS].includes(state) && (
                        <IconButton
                            iconName="editCalendar"
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedGnrRequest(params.data);
                                setShowManageGnrPlannedModal(true);
                            }}
                            title={capitalizeFirstLetter(t('editPlanned'))}
                        />
                    )}
                    {[gnrState.REQUESTED, gnrState.IN_PROGRESS].includes(state) && (
                        <IconButton
                            iconName="doNotDisturb"
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedGnrRequest(params.data);
                                setShowCancelGnrRequestModal(true);
                            }}
                            title={capitalizeFirstLetter(t('cancel'))}
                        />
                    )}
                    {[gnrState.IN_PROGRESS].includes(state) && (
                        <IconButton
                            iconName="done"
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedGnrRequest(params.data);
                                validateDeliveryModal.setTrue();
                            }}
                            title={capitalizeFirstLetter(t('validate'))}
                        />
                    )}
                    {[gnrState.DELIVERED].includes(state) && (
                        <IconButton
                            iconName="archive"
                            onClick={() => {
                                if (!params.data) return;
                                if (window.confirm(t('areYouSureYouWantToArchiveThisGnrRequest'))) {
                                    archiveGnrRequest({ id: params.data._id });
                                }
                            }}
                            title={capitalizeFirstLetter(t('archive'))}
                        />
                    )}
                </IconLine>
            );
        },
    });

    if (user?.profileId.permsLevel === permissionLevels.ADMIN) {
        columnDefs.push({
            headerName: capitalizeFirstLetter(t('_id')),
            field: '_id',
            colId: '_id',
            initialWidth: 250,
            cellClass: 'usage-col-main',
            headerClass: 'usage-col-main',
        });
    }

    const selectedRequests = useMemo(() => selectedRows, [selectedRows]);

    return (
        <>
            <GenericAgGridTable
                rowData={dataGnrRequests ?? []}
                columnDefs={columnDefs}
                loading={isLoading}
                exportCsvFileName={`gnr-${getGnrStateInfos(state)}`}
                headerRightComponent={
                    <>
                        {[gnrState.REQUESTED].includes(state) && (
                            <IconButton
                                iconName="addCircle"
                                onClick={() => {
                                    setSelectedGnrRequest(null);
                                    setShowCreateGnrRequestModal(true);
                                }}
                                title={capitalizeFirstLetter(t('add'))}
                            />
                        )}
                        {[gnrState.REQUESTED, gnrState.IN_PROGRESS].includes(state) && (
                            <IconButton
                                iconName="email"
                                onClick={() => setShowConfirmDeliveryModal(true)}
                                title={capitalizeFirstLetter(t('send'))}
                                disabled={!selectedRequests.length}
                            />
                        )}
                        {[gnrState.DELIVERED].includes(state) && (
                            <ExportCsvIcon
                                initDate={new Date()}
                                onExport={(startDate, endDate, fileName, delimiter) =>
                                    exportCsv({ exportType: exportTypeEnum.GNR, queries: { startDate, endDate, delimiter, fileName } })
                                }
                                exportIsLoading={isExportCsvLoading}
                                subTitle={capitalizeFirstLetter(t('gnrRequests'))}
                            />
                        )}
                    </>
                }
                agGridProps={{
                    rowSelection: { mode: 'multiRow' },
                    onRowSelected: (event) => {
                        const selectedRows = event.api.getSelectedRows();
                        const selectedIds = selectedRows.map((row: any) => row._id);
                        setSelectedRows(selectedIds);
                    },
                    getRowId: ({ data }) => data._id,
                }}
                storageKey={storageKey}
            />

            {showConfirmDeliveryModal && (
                <ConfirmGnrRequestModal
                    gnrRequests={dataGnrRequests?.filter((gnr) => selectedRequests.includes(gnr._id)) || []}
                    handleClose={() => setShowConfirmDeliveryModal(false)}
                    open={showConfirmDeliveryModal}
                    state={state}
                    refetch={refetch}
                />
            )}

            {showManageGnrPlannedModal && (
                <ManageGnrPlannedModal
                    gnrRequest={selectedGnrRequest}
                    refetch={refetch}
                    open={showManageGnrPlannedModal}
                    handleClose={() => setShowManageGnrPlannedModal(false)}
                />
            )}

            {showCancelGnrRequestModal && (
                <ModalBody
                    title={t('gnrRequest')}
                    subTitle={t('cancel')}
                    open={showCancelGnrRequestModal}
                    handleClose={() => setShowCancelGnrRequestModal(false)}
                    okText={t('validate')}
                    handleOk={() => mutateGnrRequest({ gnrRequestId: selectedGnrRequest?._id || '', data: { state: gnrState.CANCELED } })}
                >
                    {t('youWillCancelGnrRequest')}
                </ModalBody>
            )}

            {showDeliveryNoteModal && (
                <ModalBody
                    title={t('gnrRequest')}
                    subTitle={t('deliveryNote')}
                    open={showDeliveryNoteModal}
                    handleClose={() => setShowDeliveryNoteModal(false)}
                    okText={t('validate')}
                    isOkVisible={false}
                >
                    <img src={selectedGnrRequest?.deliveryReceiptPhotoFile?.url} alt={selectedGnrRequest?.deliveryReceiptPhotoFile?.fileName} />
                </ModalBody>
            )}

            {showCreateGnrRequestModal && (
                <ManageGnrRequestModal
                    gnrRequest={selectedGnrRequest}
                    refetch={refetch}
                    handleClose={() => setShowCreateGnrRequestModal(false)}
                    open={showCreateGnrRequestModal}
                />
            )}
            {validateDeliveryModal.value && (
                <ValidateDeliveryGnrRequestModal
                    gnrRequest={selectedGnrRequest}
                    open={validateDeliveryModal.value}
                    handleClose={validateDeliveryModal.setFalse}
                    refetch={refetch}
                />
            )}
        </>
    );
};

export default GnrTable;
